'use client';

import { useJsApiLoader } from '@react-google-maps/api';
import { useRef } from 'react';

import { getGoogleKeyApi } from './google-enviroment';

import { formatGoogleGeocoderToPlace } from '@/app/(main)/(pages)/(home)/_components/location/_consts/format-google-geocoder-to-place.const';
import { AutocompletePrediction, Place } from '@/types/google-maps.types';

export function useGoogleMaps() {
  const { isLoaded } = useJsApiLoader({
    id: 'medprev-google-maps',
    googleMapsApiKey: getGoogleKeyApi() ?? '',
    language: 'pt-BR',
    region: 'BR',
  });

  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken>(undefined);

  async function setupToken() {
    const { AutocompleteSessionToken } = await google.maps.importLibrary('places') as google.maps.PlacesLibrary;

    const token = new AutocompleteSessionToken();

    sessionTokenRef.current = token;
  }

  async function getGoogleMapsPredictions(search: string): Promise<AutocompletePrediction[]> {
    if (isLoaded && !sessionTokenRef.current) {
      setupToken();
    }

    const { AutocompleteService } = await google.maps.importLibrary('places') as google.maps.PlacesLibrary;

    const autocompleteService = new AutocompleteService();

    const { predictions } = await autocompleteService.getPlacePredictions({
      input: search,
      sessionToken: sessionTokenRef.current,
      types: ['geocode'],
      language: 'pt-BR',
      componentRestrictions: { country: 'br' },
    });

    return predictions?.map((prediction) => ({
      description: prediction.description,
      placeId: prediction.place_id,
      types: prediction.types || [],
      formattedAddress: prediction.description,
    }));
  }

  async function getGoogleMapsPlaceDetailsWithGeocoder(placeId: string): Promise<Place> {
    const geocoderService = new google.maps.Geocoder();

    const matchedPlace = await geocoderService.geocode({ placeId });

    const firstResult = matchedPlace.results[0];

    const formattedResponse = formatGoogleGeocoderToPlace([firstResult]);

    return formattedResponse;
  }

  async function getGoogleMapsPlaceByCoordinates(): Promise<Place> {
    const position = await new Promise<GeolocationPosition>((resolve, reject) => {
      navigator.geolocation.getCurrentPosition((position) => {
        resolve(position);
      }, reject);
    });

    const geocoderService = new google.maps.Geocoder();

    const geocoderResponse = await geocoderService.geocode({
      location: {
        lat: position.coords.latitude,
        lng: position.coords.longitude,
      },
    });

    return formatGoogleGeocoderToPlace(geocoderResponse.results);
  };

  async function getGoogleMapsPlaceByCityAndState(city: string, state: string): Promise<Place | null> {
    if (typeof window === 'undefined' || !window.google?.maps?.Geocoder) {
      return null;
    }

    const geocoderService = new google.maps.Geocoder();

    const fullAddress = `${city}, ${state}, Brasil`;

    const geocoderResponse = await geocoderService.geocode({ address: fullAddress });

    if (!geocoderResponse.results?.length) {
      return null;
    }

    return formatGoogleGeocoderToPlace(geocoderResponse.results);
  }

  return {
    getGoogleMapsPredictions,
    getGoogleMapsPlaceDetailsWithGeocoder,
    getGoogleMapsPlaceByCoordinates,
    getGoogleMapsPlaceByCityAndState,
  };
}
